import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import {
    Button,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    makeStyles,
    Typography,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableFooter,
    FormHelperText,
    Theme,
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import gray from '@material-ui/core/colors/grey';
import { v1 as uuid } from 'uuid';
import classNames from 'classnames';
import moment from 'moment';
import DatePicker from '../DatePicker';
import { Account, Fee, FeeType, FeeTypeList, InvoiceType, NewJournalEntry } from '../../store/modules/accounting/types';
import InputTextComponent from '../InputTextField';
import { displayCurrencyAmount, getPersonSimpleName } from '../../utils/utils';
import { ACCOUNTING_ACCOUNTS_RECEIVABLE, ACCOUNTING_UNCATEGORIZED_REVENUE } from '../../store/app.constants';
import { Contact, Person } from '../../store/modules/person/types';
import { Address, DropDownField, License } from '../../store/modules/application/types';
import { Api } from '../../store/services/services';
import { useDispatch } from 'react-redux';
import { useAuthenticationContext } from '../../authentication';
import { siteActions } from '../../store/actions';
import AutoCompleteSelect from '../AutoCompleteSelect';

type Recipient = {
    label: string;
    value: string;
    billTo: {
        name: string;
        company?: string;
        address?: Address;
        email?: string;
        phone?: string;
    };
};

type NewInvoiceDialogProps = {
    onClose: Function;
    onSave: Function;
    person: Person;
    license?: License;
    getContacts: Function;
    contacts: Contact[];
    contactsLoading: boolean;
    getInvoiceTypes: Function;
    invoiceTypes: InvoiceType[];
    invoiceTypesLoading?: boolean;
    getFeeTypes: Function;
    feeTypes: FeeTypeList;
    updating?: boolean;
};

type FeeViewModel = {
    key: string;
    date: string;
    dateError?: boolean;
    type: string;
    typeError?: boolean;
    description: string;
    qty: string;
    rate: string;
    amount: string;
    amountError?: boolean;
    loading?: boolean;
};

const predefinedTerms = {
    onReceipt: {
        label: 'Due on receipt',
        days: 0,
    },
    net30: {
        label: 'Net 30',
        days: 30,
    },
    net45: {
        label: 'Net 45',
        days: 45,
    },
    net60: {
        label: 'Net 60',
        days: 60,
    },
};

function createFee(): FeeViewModel {
    return {
        key: uuid(),
        date: '',
        type: '',
        description: '',
        qty: '',
        rate: '',
        amount: '',
    };
}

function validateFee(fee: FeeViewModel, validatePartial: boolean) {
    if ((fee.type && fee.amount) || (!fee.type && !fee.amount)) {
        fee.typeError = false;
        fee.amountError = false;
    } else if (validatePartial) {
        if (fee.type && !fee.amount) {
            fee.amountError = true;
        } else if (!fee.type && fee.amount) {
            fee.typeError = true;
        }
    }
}

function isFeeDataValid(fees: FeeViewModel[]): boolean {
    // Data is valid if there is at least one entry with data and none of the other entries have errors.
    return (
        !fees.some((fee) => fee.dateError || fee.typeError || fee.amountError) &&
        fees.some((fee) => fee.type && fee.amount)
    );
}

const useStyles = makeStyles((theme: Theme) => ({
    dialogTitle: {
        display: 'flex',
        alignItems: 'baseline',
        color: theme.palette.text.secondary,
        fontSize: 20,
        fontWeight: 500,
        marginBottom: 20,
    },
    spacer: {
        flexGrow: 1,
    },
    dialogContent: {
        minHeight: 400,
    },
    tableRow: {
        verticalAlign: 'top',
    },
    tableFooterCell: {
        borderBottomWidth: 0,
    },
    label: {
        fontSize: 14,
        fontWeight: 500,
        color: theme.palette.text.secondary,
        textAlign: 'right',
        marginRight: theme.spacing(2),
    },
    datePicker: {
        margin: 0,
    },
    datePickerContainer: {
        backgroundColor: 'white',
        borderRadius: 5,
        justifyContent: 'flex-start',
    },
    datePickerInput: {
        fontSize: 13,
        border: '1px solid rgb(0,0,0,0.23) !important',
        paddingTop: 7,
        paddingBottom: 3,
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        borderRadius: 3,
        marginBottom: '5px',
        color: '#484848 !important',
        width: 150,
        '&:before': {
            borderBottomColor: 'white',
        },
        '&:after': {
            borderBottomColor: 'white',
        },
        '&:hover:before': {
            borderBottomColor: ['white', '!important'],
        },
    },
    selectInput: {
        fontSize: 13,
        border: '1px solid rgb(0,0,0,0.23) !important',
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        borderRadius: 3,
        marginBottom: '5px',
        color: '#484848 !important',
        minWidth: 150,
    },
    recipientInputRoot: {
        width: 180,
    },
    invoiceTypeContainer: {
        display: 'flex',
        alignItems: 'center',
    },
    textField: {
        fontSize: 14,
        fontWeight: 450,
        color: gray[600],
        lineHeight: '21px',
        wordWrap: 'break-word',
        width: '100%',
    },
    textFieldInput: {
        fontSize: 13,
        border: '1px solid rgb(0,0,0,0.23) !important',
        padding: 10,
        borderRadius: 3,
        marginBottom: '5px',
        color: '#484848 !important',
    },
    amountInput: {
        textAlign: 'right',
    },
    fullWidth: {
        width: '100%',
    },
    required: {
        '&:after': {
            content: '" *"',
            color: 'red',
        },
    },
    feeTypeInput: {
        marginRight: '-13px',
    },
    invoiceTypeInputRoot: {
        width: 200,
    },
    predefinedTermInputRoot: {
        width: '150px',
        marginBottom: '5px',
    },
}));

const RECIPIENT_PERSON = '__person';

const NewInvoiceDialog: FunctionComponent<NewInvoiceDialogProps> = (props) => {
    const {
        onClose,
        onSave,
        person,
        license,
        getContacts,
        contacts,
        contactsLoading,
        getInvoiceTypes,
        invoiceTypes,
        invoiceTypesLoading,
        getFeeTypes,
        feeTypes,
        updating,
    } = props;

    const dispatch = useDispatch();
    const authContext = useAuthenticationContext();

    const personRecipient = useMemo<Recipient>(
        () => ({
            label: getPersonSimpleName(person.name),
            value: RECIPIENT_PERSON,
            billTo: {
                name: getPersonSimpleName(person.name),
                address: person.address
                    ? {
                          line1: person.address.line1,
                          line2: person.address.line2,
                          city: person.address.city,
                          county: person.address.county,
                          state: person.address.state,
                          zipcode: person.address.zipcode,
                      }
                    : undefined,
                email: person.email,
                phone: person.phone,
            },
        }),
        [person],
    );

    const [invoiceType, setInvoiceType] = useState<DropDownField | null>(null);
    const [date, setDate] = useState(moment().format());
    const [dateInvalid, setDateInvalid] = useState(false);
    const [recipient, setRecipient] = useState(personRecipient);
    const [recipientList, setRecipientList] = useState([personRecipient]);
    const [dueDate, setDueDate] = useState(date);
    const [dueDateInvalid, setDueDateInvalid] = useState(false);
    const [terms, setTerms] = useState<DropDownField | null>({ label: 'Due on receipt', value: 'onReceipt' });
    const [fees, setFees] = useState<FeeViewModel[]>([createFee(), createFee()]);

    const classes = useStyles();
    useEffect(() => {
        getContacts(person.id);
        getInvoiceTypes();
        getFeeTypes();
    }, [getInvoiceTypes, getFeeTypes, getContacts, person.id]);

    useEffect(() => {
        if (!invoiceTypesLoading) {
            invoiceTypes[0] && setInvoiceType({ label: invoiceTypes[0].name, value: invoiceTypes[0].id });
        }
    }, [invoiceTypes, invoiceTypesLoading]);

    useEffect(() => {
        if (!contactsLoading) {
            const contactRecipients = contacts.map((contact) => {
                return {
                    label: `${getPersonSimpleName(contact.name)}`,
                    value: contact.id,
                    billTo: {
                        name: getPersonSimpleName(contact.name),
                        company: contact.company,
                        address: contact.address
                            ? {
                                  line1: contact.address.line1,
                                  line2: contact.address.line2,
                                  city: contact.address.city,
                                  county: contact.address.county,
                                  state: contact.address.state,
                                  zipcode: contact.address.zipcode,
                              }
                            : undefined,
                        email: contact.email,
                    },
                };
            });

            contactRecipients.sort((recipient1, recipient2) => {
                return recipient1.label.localeCompare(recipient2.label);
            });

            setRecipientList([personRecipient, ...contactRecipients]);
        }
    }, [contacts, contactsLoading, personRecipient]);

    const adjustDueDate = (date: string, data: DropDownField | null) => {
        const invoiceDate = moment(date);

        if (invoiceDate.isValid()) {
            if (terms) {
                const days = data?.value && predefinedTerms[data.value as keyof typeof predefinedTerms].days;

                setDueDate(invoiceDate.add(days, 'days').format());
            } else {
                // Make sure due date is not before invoice date.
                const dueDateMoment = moment(dueDate);

                if (dueDateMoment.isValid() && dueDateMoment.isBefore(invoiceDate)) {
                    setDueDate(date);
                }
            }
        }
    };

    const changeInvoiceType = (data: DropDownField | null) => {
        setInvoiceType(data);
    };

    const changeDate = (date: string) => {
        setDate(date);
        setDateInvalid(!moment(date).isValid());
        terms && adjustDueDate(date, terms);
    };

    const changeRecipient = (data: DropDownField | null) => {
        const recipient = recipientList.find((recipient: Recipient) => recipient.value === data?.value);
        setRecipient(recipient ?? personRecipient);
    };

    const changeTerms = (data: DropDownField | null) => {
        adjustDueDate(date, data);
        setTerms(data);
    };

    const changeDueDate = (dueDate: string) => {
        setTerms(null);
        setDueDate(dueDate);
        setDueDateInvalid(!!dueDate && !moment(dueDate).isValid());
    };

    const generateFee = async (feeType: FeeType, viewModel: FeeViewModel): Promise<Fee | undefined> => {
        try {
            viewModel.loading = true;

            const token = await authContext.getAccessToken({ scopes: [authContext.scopes['feeTypes.read']] });
            const data =
                feeType.entityType === 'License' ? license : feeType.entityType === 'Person' ? person : undefined;

            return Api.generateFee(token.accessToken, feeType.id, data);
        } catch (err) {
            dispatch(siteActions.setError(err));

            return undefined;
        } finally {
            viewModel.loading = false;
        }
    };

    const updateFee = async (
        key: string,
        property: keyof Omit<FeeViewModel, 'key' | 'dateError' | 'typeError' | 'amountError' | 'loading'>,
        value: string | null,
    ) => {
        const updatedFees = [...fees];
        const fee = updatedFees.find((fee) => fee.key === key);

        if (fee) {
            // Check if removing value from a property that currently has a value.
            const valueRemoved: boolean = !!fee[property] && !value;
            if (value !== null) {
                fee[property] = value;
            } else {
                fee[property] = '';
            }

            // Only validate partial entries if a value was removed.  This avoids showing
            // errors while the user is entering data, i.e. showing errors before the user
            // has entered all the fields.
            validateFee(fee, valueRemoved);

            switch (property) {
                case 'date':
                    fee.dateError = !!fee.date && !moment(fee.date).isValid();
                    break;

                case 'type':
                    // Populate fee details.
                    const feeType = feeTypes.find((feeType) => feeType.id === fee.type);

                    if (feeType) {
                        const feeDetails = await generateFee(feeType, fee);

                        fee.description = feeDetails?.description || '';
                        fee.qty = feeDetails?.quantity?.toString() || '';
                        fee.rate = feeDetails?.unitPrice?.toFixed(2) || '';
                        fee.amount = feeDetails?.amount?.toFixed(2) || '';
                    }

                    break;

                case 'qty':
                    // If rate is also specified, recalculate amount.
                    if (fee.qty && fee.rate) {
                        fee.amount = (+fee.qty * +fee.rate).toFixed(2);
                    }

                    break;

                case 'rate':
                    // Recalculate amount.  If no qty specified, assume it is 1.
                    if (fee.rate) {
                        fee.amount = ((+fee.qty || 1) * +fee.rate).toFixed(2);
                    }

                    break;

                case 'amount':
                    // If rate is specified, adjust the rate to be consistent with the
                    // new amount.
                    if (fee.amount && fee.rate) {
                        fee.rate = (+fee.amount / (+fee.qty || 1)).toFixed(2);
                    }

                    break;
            }

            if (updatedFees[updatedFees.length - 1].type) {
                // Add new blank entry if everything is filled out.
                updatedFees.push(createFee());
            }

            setFees(updatedFees);
        }
    };

    const handleAdd = () => {
        if (!invoiceType || dateInvalid || dueDateInvalid) {
            return;
        }

        // Re-run validation since the user may have been in the middle of entering an entry.
        const validatedFees = [...fees];

        validatedFees.forEach((fee) => validateFee(fee, true));

        if (!isFeeDataValid(validatedFees)) {
            setFees(validatedFees);

            return;
        }

        const dueDateMoment = moment(dueDate);

        const journalEntry: NewJournalEntry = {
            type: 'Invoice',
            date: moment(date).format('YYYY-MM-DD'),
            invoicing: {
                type: {
                    id: invoiceType.value,
                    name: invoiceType.label,
                },
                dueDate: dueDateMoment.isValid() ? dueDateMoment.format('YYYY-MM-DD') : undefined,
                billTo: recipient?.billTo,
            },
            lineItems: [],
        };

        for (const fee of fees.filter((fee) => fee.type && fee.amount)) {
            const units: number | undefined = fee.qty ? parseFloat(fee.qty) : undefined;
            const unitPrice: number | undefined = fee.rate ? parseFloat(fee.rate) : undefined;
            const amount = parseFloat(fee.amount);
            const feeType = feeTypes.find((feeType) => feeType.id === fee.type)!;
            const revenueAccount: Account = feeType.revenueAccount
                ? { ...feeType.revenueAccount, type: 'Revenue' }
                : {
                      id: ACCOUNTING_UNCATEGORIZED_REVENUE,
                      name: 'Uncategorized Revenue',
                      type: 'Revenue',
                  };

            journalEntry.lineItems.push({
                type: 'credit',
                units,
                unitPrice,
                amount,
                description: fee.description,
                serviceDate: fee.date || undefined,
                account: revenueAccount,
                feeType: {
                    id: feeType.id,
                    name: feeType.name,
                },
            });

            journalEntry.lineItems.push({
                type: 'debit',
                units,
                unitPrice,
                amount,
                description: fee.description,
                serviceDate: fee.date || undefined,
                account: {
                    id: ACCOUNTING_ACCOUNTS_RECEIVABLE,
                    name: 'Accounts Receivable',
                    type: 'Asset',
                },
                feeType: {
                    id: feeType.id,
                    name: feeType.name,
                },
            });
        }
        onSave(journalEntry);
    };

    const isValid = invoiceType && !dateInvalid && !dueDateInvalid && isFeeDataValid(fees);
    const total = fees.reduce((accum, fee) => accum + (fee.amount ? parseFloat(fee.amount) : 0), 0);
    return (
        <>
            <DialogTitle className={classes.dialogTitle} disableTypography>
                <Typography variant="h6">New Invoice</Typography>
                <div className={classes.spacer} />
                <div className={classes.invoiceTypeContainer}>
                    {!invoiceTypesLoading ? (
                        invoiceTypes?.length ? (
                            <>
                                <Typography className={classes.label}>Invoice Type</Typography>
                                <AutoCompleteSelect
                                    data={invoiceTypes.map((type) => ({
                                        label: type.name,
                                        value: type.id,
                                    }))}
                                    isMultipleValue={false}
                                    onChange={changeInvoiceType}
                                    inputSize={'small'}
                                    disableClearable={true}
                                    value={invoiceType}
                                    classes={{
                                        inputRoot: classes.invoiceTypeInputRoot,
                                    }}
                                    variant={'outlined'}
                                    ariaLabel="Invoice Type"
                                />
                            </>
                        ) : (
                            <></>
                        )
                    ) : (
                        <Skeleton variant="text" width="30%" />
                    )}
                </div>

                {!invoiceTypesLoading && !invoiceTypes?.length && (
                    <Typography color="error">No invoice types configured</Typography>
                )}
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>
                <Grid container>
                    <Grid item container xs={6} alignItems="center">
                        <Grid item xs={3}>
                            <Typography className={classes.label}>Date</Typography>
                        </Grid>
                        <Grid item xs={9}>
                            <DatePicker
                                containerClasses={classes.datePickerContainer}
                                datePickerClasses={classes.datePicker}
                                inputProps={{
                                    classes: {
                                        root: classes.datePickerInput,
                                    },
                                }}
                                label=""
                                value={date}
                                error={dateInvalid}
                                handleDate={changeDate}
                            />
                            {!date && <FormHelperText error>Date is required</FormHelperText>}
                        </Grid>
                        <Grid item xs={3}>
                            <Typography className={classes.label}>Terms</Typography>
                        </Grid>
                        <Grid item xs={9}>
                            <AutoCompleteSelect
                                data={Object.entries(predefinedTerms).map((entry) => ({
                                    label: entry[1].label,
                                    value: entry[0],
                                }))}
                                onChange={changeTerms}
                                inputSize={'small'}
                                value={terms}
                                classes={{
                                    inputRoot: classes.predefinedTermInputRoot,
                                }}
                                disableClearable={true}
                                variant={'outlined'}
                                ariaLabel="Terms"
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <Typography className={classes.label}>Due Date</Typography>
                        </Grid>
                        <Grid item xs={9}>
                            <DatePicker
                                containerClasses={classes.datePickerContainer}
                                datePickerClasses={classes.datePicker}
                                inputProps={{
                                    classes: {
                                        root: classes.datePickerInput,
                                    },
                                }}
                                label=""
                                value={dueDate}
                                error={dueDateInvalid}
                                handleDate={changeDueDate}
                            />
                        </Grid>
                    </Grid>
                    <Grid item container xs={6} alignItems="center">
                        <Grid item xs={3}>
                            <Typography className={classes.label}>Recipient</Typography>
                        </Grid>
                        <Grid item xs={9}>
                            <AutoCompleteSelect
                                data={recipientList.map((recipient) => ({
                                    label: recipient.label,
                                    value: recipient.value,
                                }))}
                                onChange={changeRecipient}
                                inputSize={'small'}
                                value={
                                    recipient.value
                                        ? {
                                              label: recipient.label,
                                              value: recipient.value,
                                          }
                                        : null
                                }
                                classes={{
                                    inputRoot: classes.recipientInputRoot,
                                }}
                                disableClearable={true}
                                variant={'outlined'}
                                ariaLabel="Recipient"
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <Typography className={classes.label}>Send To</Typography>
                        </Grid>
                        <Grid item xs={9}>
                            <Typography>{recipient.billTo.name}</Typography>
                            {recipient.billTo.company && <Typography>{recipient.billTo.company}</Typography>}
                            <Typography>{recipient.billTo.address?.line1}</Typography>
                            <Typography>{recipient.billTo.address?.line2}</Typography>
                            <Typography>
                                {recipient.billTo?.address?.city} {recipient.billTo.address?.state}{' '}
                                {recipient.billTo.address?.zipcode}
                            </Typography>
                        </Grid>
                    </Grid>
                </Grid>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell width="15%">Service Date</TableCell>
                                <TableCell width="20%">
                                    <span className={classes.required}>Item</span>
                                </TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell align="right" size="small" width="10%">
                                    Qty
                                </TableCell>
                                <TableCell align="right" size="small" width="10%">
                                    Rate
                                </TableCell>
                                <TableCell align="right" size="small" width="13%">
                                    <span className={classes.required}>Amount</span>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {fees.map((fee) => (
                                <TableRow key={fee.key} className={classes.tableRow}>
                                    <TableCell size="small">
                                        <DatePicker
                                            containerClasses={classes.datePickerContainer}
                                            datePickerClasses={classes.datePicker}
                                            inputProps={{
                                                classes: {
                                                    root: classes.datePickerInput,
                                                },
                                            }}
                                            label=""
                                            value={fee.date}
                                            error={fee.dateError}
                                            handleDate={(date: string) => {
                                                updateFee(fee.key, 'date', date);
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell size="small">
                                        <AutoCompleteSelect
                                            data={
                                                Array.isArray(feeTypes)
                                                    ? feeTypes?.map((feeType) => ({
                                                          label: feeType.name,
                                                          value: feeType.id,
                                                      }))
                                                    : []
                                            }
                                            onChange={(data: DropDownField | null) => {
                                                const value = data !== null ? data.value : null;
                                                updateFee(fee.key, 'type', value);
                                            }}
                                            inputSize={'small'}
                                            classes={{
                                                input: classes.feeTypeInput,
                                            }}
                                            variant={'outlined'}
                                            ariaLabel="Type"
                                        />
                                        {fee.typeError && <FormHelperText error>Type is required</FormHelperText>}
                                    </TableCell>
                                    <TableCell size="small">
                                        <InputTextComponent
                                            id={`description-${fee.key}`}
                                            disabled={fee.loading}
                                            size="small"
                                            fontSize="13"
                                            className={classes.textField}
                                            inputProps={classes.textFieldInput}
                                            onChange={(description: string) => {
                                                updateFee(fee.key, 'description', description);
                                            }}
                                            value={fee.description}
                                        />
                                    </TableCell>
                                    <TableCell align="right" size="small">
                                        <InputTextComponent
                                            id={`qty-${fee.key}`}
                                            disabled={fee.loading}
                                            size="small"
                                            fontSize="13"
                                            className={classes.textField}
                                            inputProps={classNames(classes.textFieldInput, classes.amountInput)}
                                            onChange={(qty: string) => updateFee(fee.key, 'qty', qty)}
                                            value={fee.qty}
                                            inputType={'number'}
                                        />
                                    </TableCell>
                                    <TableCell align="right" size="small">
                                        <InputTextComponent
                                            id={`rate-${fee.key}`}
                                            disabled={fee.loading}
                                            size="small"
                                            fontSize="13"
                                            className={classes.textField}
                                            inputProps={classNames(classes.textFieldInput, classes.amountInput)}
                                            onChange={(rate: string) => updateFee(fee.key, 'rate', rate)}
                                            value={fee.rate}
                                            inputType={'currency'}
                                        />
                                    </TableCell>
                                    <TableCell align="right" size="small">
                                        <InputTextComponent
                                            id={`amount-${fee.key}`}
                                            disabled={fee.loading}
                                            size="small"
                                            fontSize="13"
                                            className={classes.textField}
                                            inputProps={classNames(classes.textFieldInput, classes.amountInput)}
                                            onChange={(amount: string) => updateFee(fee.key, 'amount', amount)}
                                            value={fee.amount}
                                            inputType={'currency'}
                                            error={fee.amountError}
                                        />
                                        {fee.amountError && <FormHelperText error>Amount is required</FormHelperText>}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                        <TableFooter>
                            <TableRow>
                                <TableCell align="right" size="small" colSpan={6} className={classes.tableFooterCell}>
                                    <Typography component="span" className={classes.label}>
                                        Total:
                                    </Typography>
                                    <Typography component="span">{displayCurrencyAmount(total)}</Typography>
                                </TableCell>
                            </TableRow>
                        </TableFooter>
                    </Table>
                </TableContainer>
            </DialogContent>
            <DialogActions>
                <Button disableElevation onClick={() => onClose()}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disableElevation
                    color="primary"
                    disabled={!isValid || updating}
                    onClick={handleAdd}
                >
                    Add
                </Button>
            </DialogActions>
        </>
    );
};

export default NewInvoiceDialog;
